import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BottomSheet } from '../../shared/bottom-sheet';
import { cn } from '../../../lib/utils';
import { previousPeriod } from '../documents.utils';
import { PayslipPeriodStep } from './PayslipPeriodStep';
import { PayslipMatchStep, type PayslipGroup } from './PayslipMatchStep';
import { PayslipUploadStep } from './PayslipUploadStep';
import { usePayslipUploadJobs, type PayslipPeriod } from './usePayslipUploadJobs';

export interface PayslipPerson {
  id: number;
  name: string;
  codiceFiscale?: string | null;
  isActive?: boolean;
}

interface PayslipUploadSheetProps {
  open: boolean;
  people: PayslipPerson[];
  onClose: () => void;
  onDone: () => void;
}

type Step = 'period' | 'match' | 'upload';

const initialPeriod = (): PayslipPeriod => {
  const { year, month } = previousPeriod();
  return { type: 'MENSILE', year, month };
};

/** Three-step payslip wizard; works with a single one-employee PDF as well as a multi-employee batch. */
export const PayslipUploadSheet: React.FC<PayslipUploadSheetProps> = ({ open, people, onClose, onDone }) => {
  const { t } = useTranslation();
  const [step, setStep] = useState<Step>('period');
  const [period, setPeriod] = useState<PayslipPeriod>(initialPeriod);
  const [files, setFiles] = useState<File[]>([]);
  const [extractBalances, setExtractBalances] = useState(true);
  const [groups, setGroups] = useState<PayslipGroup[]>([]);
  const queue = usePayslipUploadJobs(period, extractBalances);

  useEffect(() => {
    if (open) {
      setStep('period');
      setPeriod(initialPeriod());
      setFiles([]);
      setExtractBalances(true);
      setGroups([]);
    }
  }, [open]);

  const startUpload = useCallback(() => {
    const jobs = groups
      .filter((group) => group.userId !== null)
      .map((group) => {
        const person = people.find((p) => p.id === group.userId);
        return {
          id: group.id,
          file: group.file,
          userId: group.userId as number,
          userName: person?.name ?? `#${group.userId}`,
          userInactive: person?.isActive === false,
        };
      });
    setStep('upload');
    void queue.start(jobs);
  }, [groups, people, queue]);

  const steps: Array<{ key: Step; label: string }> = [
    { key: 'period', label: t('documents.payslipUpload.steps.period', 'Periodo e file') },
    { key: 'match', label: t('documents.payslipUpload.steps.match', 'Abbinamento') },
    { key: 'upload', label: t('documents.payslipUpload.steps.upload', 'Caricamento') },
  ];

  const close = () => {
    if (step === 'upload' && queue.running) return;
    onClose();
  };

  return (
    <BottomSheet open={open} onClose={close} size="wide" title={t('documents.payslipUpload.title', 'Carica buste paga')}>
      <ol className="mb-4 flex gap-2 text-xs">
        {steps.map((item, index) => (
          <li
            key={item.key}
            className={cn(
              'flex items-center gap-1.5 rounded-full border px-2.5 py-1',
              item.key === step ? 'border-orange-500 bg-orange-50 text-orange-700 dark:bg-orange-950/30' : 'text-muted-foreground',
            )}
          >
            <span className="font-semibold">{index + 1}</span>
            <span className="hidden sm:inline">{item.label}</span>
          </li>
        ))}
      </ol>

      {step === 'period' && (
        <PayslipPeriodStep
          period={period}
          onPeriodChange={setPeriod}
          files={files}
          onFilesChange={setFiles}
          extractBalances={extractBalances}
          onExtractChange={setExtractBalances}
          onNext={() => setStep('match')}
        />
      )}
      {step === 'match' && (
        <PayslipMatchStep files={files} people={people} groups={groups} onGroupsChange={setGroups} onBack={() => setStep('period')} onNext={startUpload} />
      )}
      {step === 'upload' && (
        <PayslipUploadStep
          jobs={queue.jobs}
          running={queue.running}
          summary={queue.summary}
          onResolveConflict={queue.resolveConflict}
          onReplaceAll={queue.replaceAll}
          onRetry={queue.retry}
          onFinish={onDone}
        />
      )}
    </BottomSheet>
  );
};
